import React, { useContext, useState, useRef } from 'react';
import { GlobalContext } from '../../components/contexts';

const FileUpload = ({
  onClose,
  dbForm,
  dbPost,
  onUpload,
  selectedFiles: externalSelectedFiles,
  setSelectedFiles: externalSetSelectedFiles,
  uploadedFiles: externalUploadedFiles,
  setUploadedFiles: externalSetUploadedFiles,
  sharedFileId: externalSharedFileId,
  setSharedFileId: externalSetSharedFileId,
  currentFileSeq: externalCurrentFileSeq,
  setCurrentFileSeq: externalSetCurrentFileSeq,
}) => {
  const { store } = useContext(GlobalContext);
  const [internalSelectedFiles, setInternalSelectedFiles] = useState([]);
  const [internalUploadedFiles, setInternalUploadedFiles] = useState([]);
  const [internalSharedFileId, setInternalSharedFileId] = useState(null);
  const [internalCurrentFileSeq, setInternalCurrentFileSeq] = useState(null);

  const selectedFiles = externalSelectedFiles ?? internalSelectedFiles;
  const setSelectedFiles = externalSetSelectedFiles ?? setInternalSelectedFiles;
  const uploadedFiles = externalUploadedFiles ?? internalUploadedFiles;
  const setUploadedFiles = externalSetUploadedFiles ?? setInternalUploadedFiles;
  const sharedFileId = externalSharedFileId ?? internalSharedFileId;
  const setSharedFileId = externalSetSharedFileId ?? setInternalSharedFileId;
  const currentFileSeq = externalCurrentFileSeq ?? internalCurrentFileSeq;
  const setCurrentFileSeq =
    externalSetCurrentFileSeq ?? setInternalCurrentFileSeq;

  const fileInputRef = useRef(null);

  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const newFiles = files.map(file => ({
      name: file.name,
      size: Math.round(file.size / 1024),
      type: file.type,
      status: 'Pending',
      file,
      errorMessage: '',
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);

    checkResolutionAndUploadFiles(newFiles);
  };

  const checkResolutionAndUploadFiles = async files => {
    const validFiles = [];
    const invalidExtensions = [
      '.java',
      '.jsp',
      '.jspx',
      '.exe',
      '.js',
      '.jsx',
      '.ts',
      '.tsx',
      '.sh',
      '.bat',
      '.cmd',
      '.php',
      '.asp',
      '.aspx',
      '.cgi',
      '.pl',
      '.py',
      '.rb',
      '.war',
      '.jar',
      '.html',
      '.htm',
      '.shtml',
      '.xhtml',
      '.sql',
      '.db',
      '.sqlite',
      '.mdb',
      '.accdb',
      '.ini',
      '.reg',
      '.sys',
      '.dll',
    ];

    for (const file of files) {
      const fileExtension = file.name
        .slice(file.name.lastIndexOf('.'))
        .toLowerCase();

      if (invalidExtensions.includes(fileExtension)) {
        updateFileStatus(file.name, 'Failed', '지원하지 않는 파일 형식입니다.');
        continue;
      }

      if (file.type.startsWith('video/')) {
        const fileSizeInMB = file.size / 1024;
        if (fileSizeInMB < 10 || fileSizeInMB > 250) {
          updateFileStatus(
            file.name,
            'Failed',
            '파일 크기는 10MB ~ 250MB 사이여야 합니다.'
          );
          continue;
        }

        const videoElement = document.createElement('video');
        const fileURL = URL.createObjectURL(file.file);
        videoElement.src = fileURL;

        try {
          await new Promise((resolve, reject) => {
            videoElement.onloadedmetadata = () => {
              const width = videoElement.videoWidth;
              const height = videoElement.videoHeight;
              const isLandscape = width > height;
              const isValidResolution = isLandscape
                ? width <= 1920 &&
                  width >= 1280 &&
                  height <= 1080 &&
                  height >= 720
                : width <= 1080 &&
                  width >= 720 &&
                  height <= 1920 &&
                  height >= 1280;

              if (!isValidResolution) {
                const orientation = isLandscape ? '가로' : '세로';
                updateFileStatus(
                  file.name,
                  'Failed',
                  `${orientation} 영상의 해상도가 허용 범위를 벗어났습니다.`
                );
              } else {
                validFiles.push(file);
              }
              URL.revokeObjectURL(fileURL);
              resolve();
            };
            videoElement.onerror = () => {
              updateFileStatus(
                file.name,
                'Failed',
                '비디오 메타데이터를 로드할 수 없습니다.'
              );
              URL.revokeObjectURL(fileURL);
              reject();
            };
          });
        } catch {
          continue;
        }
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      await uploadFilesSequentially(validFiles);
    }
  };

  const uploadFilesSequentially = async files => {
    let sharedFileIdLocal = sharedFileId;

    for (const file of files) {
      const uploadResponse = await uploadFile(file, sharedFileIdLocal);
      if (uploadResponse) {
        if (!sharedFileIdLocal) {
          sharedFileIdLocal = uploadResponse.fileId;
          setSharedFileId(uploadResponse.fileId);
        }

        if (uploadResponse.filePath) {
          onUpload(uploadResponse.filePath);
        }
      }
    }
  };

  const uploadFile = async (fileData, fileId) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData.file);
      formData.append('entSn', store?.entSn);
      formData.append('userSn', store?.userSn);
      formData.append('userType', store?.userType);

      if (fileId) {
        formData.append('fileId', fileId);
      }

      const uploadUrl = '/common/file/upload.do';
      const response = await dbForm(uploadUrl, formData);

      if (response && response.fileId && response.fileSeq !== undefined) {
        setUploadedFiles(prev => [
          ...prev,
          {
            name: fileData.name,
            size: fileData.size,
            fileId: response.fileId,
            fileSeq: response.fileSeq,
          },
        ]);

        updateFileStatus(fileData.name, 'Uploaded');

        return {
          fileId: response.fileId,
          fileSeq: response.fileSeq,
          filePath: response.filePath,
        };
      } else {
        updateFileStatus(fileData.name, 'Failed');
        return null;
      }
    } catch (error) {
      updateFileStatus(fileData.name, 'Failed');
      return null;
    }
  };

  const updateFileStatus = (fileName, status, errorMessage = '') => {
    setSelectedFiles(prev =>
      prev.map(file =>
        file.name === fileName ? { ...file, status, errorMessage } : file
      )
    );
  };
  const handleFileDelete = async indexToDelete => {
    const fileToDelete = uploadedFiles[indexToDelete];

    if (!fileToDelete || !fileToDelete.fileId || !fileToDelete.fileSeq) {
      return;
    }

    try {
      const deleteUrl = '/common/file/delete.do';
      const payload = {
        fileId: fileToDelete.fileId,
        fileSeq: fileToDelete.fileSeq,
      };

      const response = await dbPost(deleteUrl, payload);

      if (response) {
        setSelectedFiles(prev =>
          prev.filter((_, index) => index !== indexToDelete)
        );
        setUploadedFiles(prev =>
          prev.filter((_, index) => index !== indexToDelete)
        );
        alert('파일이 성공적으로 삭제되었습니다.');
      } else {
        alert('파일 삭제 실패: 서버에서 파일을 삭제하지 못했습니다.');
      }
    } catch (error) {
      alert('파일 삭제 중 문제가 발생했습니다.');
    }
  };

  const handleDeleteAll = async () => {
    try {
      const deletePromises = uploadedFiles.map(file => {
        const deleteUrl = '/common/file/delete.do';
        const payload = {
          fileId: file.fileId,
          fileSeq: file.fileSeq,
        };
        return dbPost(deleteUrl, payload);
      });

      const responses = await Promise.all(deletePromises);
      const allSuccess = responses.every(response => response);

      if (allSuccess) {
        alert('모든 파일이 성공적으로 삭제되었습니다.');
        setSelectedFiles([]);
        setUploadedFiles([]);
      } else {
        alert('일부 파일 삭제에 실패했습니다.');
      }
    } catch (error) {
      alert('전체 파일 삭제 중 문제가 발생했습니다.');
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleDragOver = e => {
    e.preventDefault();
  };

  const handleDrop = e => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    const newFiles = files.map(file => ({
      name: file.name,
      size: Math.round(file.size / 1024),
      file,
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);
    uploadFilesSequentially(newFiles);
  };

  return (
    <>
      <section
        id="modal_sample_03"
        className="modal shown in"
        aria-hidden="false"
        role="dialog"
        aria-labelledby="tit_modal_sample_03"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="tit_modal_sample_03" className="modal-title">
                파일 등록
              </h2>
            </div>
            <div className="modal-conts">
              <div
                className="file-upload"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                style={{
                  border: '2px dashed #ccc',
                  padding: '20px',
                  textAlign: 'center',
                }}
              >
                <p className="txt">
                  첨부할 파일을 여기에 끌어다 놓거나, 파일 선택 버튼을 눌러
                  파일을 직접 선택해주세요.
                </p>
                <button
                  type="button"
                  className="btn tertiary ico-before ico-upload md"
                  onClick={handleFileButtonClick}
                >
                  파일선택
                </button>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  aria-labelledby="input-files-label"
                />
              </div>
              <div className="file-upload-result">
                <div className="upload-top">
                  <div className="file-total">
                    <span className="current">{uploadedFiles.length}개</span> /{' '}
                    {selectedFiles.length}개
                  </div>
                </div>
                <ul className="upload-list">
                  {selectedFiles.map((file, index) => {
                    let statusClass = '';
                    if (file.status === 'Uploading') statusClass = 'spinner';
                    else if (file.status === 'Uploaded')
                      statusClass = 'ico-invalid ok';
                    else if (file.status === 'Failed') statusClass = 'is-error';

                    return (
                      <li
                        key={index}
                        className={file.status === 'Failed' ? 'is-error' : ''}
                      >
                        <div className="in">
                          <div className="file-name">
                            {file.name} [{file.size} KB]
                          </div>
                          <div className="file-btn">
                            {file.status === 'Uploading' && (
                              <span className={statusClass}></span>
                            )}
                            {file.status === 'Failed' && (
                              <span className="ico-invalid error">
                                <em className="sr-only">에러</em>
                              </span>
                            )}
                            {file.status === 'Uploaded' && (
                              <span className={statusClass}>
                                <em className="sr-only">정상</em>
                              </span>
                            )}
                            {file.status !== 'Uploading' && (
                              <button
                                type="button"
                                className="btn btn-txt ico-before ico-del sm h-auto"
                                onClick={() => handleFileDelete(index)}
                              >
                                삭제
                              </button>
                            )}
                          </div>
                        </div>
                        {file.status === 'Failed' && (
                          <p className="file-hint">
                            {file.errorMessage ||
                              '파일 업로드 중 에러가 발생했습니다.'}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {selectedFiles.length > 0 && (
                  <div className="upload-delete-btn">
                    <button
                      type="button"
                      className="btn btn-txt ico-before ico-del sm h-auto"
                      onClick={handleDeleteAll}
                    >
                      전체 파일 삭제
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-btn btn-wrap" style={{ textAlign: 'right' }}>
              <button
                type="button"
                className="btn secondary md close-modal"
                onClick={onClose}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
        <div className="modal-back in"></div>
      </section>
    </>
  );
};

export default FileUpload;
